import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

const Categories = () => {
  const navigate = useNavigate();
  const [selectedParentId, setSelectedParentId] = useState("");
  const [selectedChildId, setSelectedChildId] = useState("");
  const [selectedGrandchildId, setSelectedGrandchildId] = useState("");
  const [categories, setCategories] = useState([]);
  const [children, setChildren] = useState([]);
  const [grandchildren, setGrandchildren] = useState([]);
  const [greatGrandchildren, setGreatGrandchildren] = useState([]);
  const [displayItems, setDisplayItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const filterByTaxonomy = (items) => {
    return items.filter((item) => item.taxonomy === null);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedParentId) {
      const parent = categories.find(
        (cat) => String(cat.id) === String(selectedParentId)
      );
      const parentChildren = parent?.children || [];
      setChildren(parentChildren);
      setSelectedChildId("");
      setSelectedGrandchildId("");
      setGrandchildren([]);
      setGreatGrandchildren([]);
      setDisplayItems(parentChildren);
    } else {
      setChildren([]);
      setGrandchildren([]);
      setGreatGrandchildren([]);
      setDisplayItems(categories);
    }
  }, [selectedParentId, categories]);

  useEffect(() => {
    if (selectedChildId) {
      const child = children.find(
        (cat) => String(cat.id) === String(selectedChildId)
      );
      const childGrandchildren = child?.children || [];
      setGrandchildren(childGrandchildren);
      setSelectedGrandchildId("");
      setGreatGrandchildren([]);
      setDisplayItems(childGrandchildren);
    } else if (selectedParentId) {
      setDisplayItems(children);
      setGrandchildren([]);
    }
  }, [selectedChildId, children]);

  useEffect(() => {
    if (selectedGrandchildId) {
      const grandchild = grandchildren.find(
        (cat) => String(cat.id) === String(selectedGrandchildId)
      );
      const grandchildChildren = grandchild?.children || [];
      setGreatGrandchildren(grandchildChildren);
      setDisplayItems(grandchildChildren);
    } else if (selectedChildId) {
      setDisplayItems(grandchildren);
    }
  }, [selectedGrandchildId, grandchildren]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/fetch-categories`
      );
      const categoriesData = response.data?.data?.terms || [];
      setCategories(categoriesData);
      setSelectedParentId("");
      setSelectedChildId("");
      setSelectedGrandchildId("");
      setChildren([]);
      setGrandchildren([]);
      setGreatGrandchildren([]);
      setDisplayItems(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
      setDisplayItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/categories/${id}`
        );
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("dragIndex", index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("dragIndex"));
    if (dragIndex === dropIndex) return;

    const updatedItems = [...displayItems];
    const [movedItem] = updatedItems.splice(dragIndex, 1);
    updatedItems.splice(dropIndex, 0, movedItem);

    const reordered = updatedItems.map((item, index) => ({
      ...item,
      position: index + 1,
    }));

    setDisplayItems(reordered);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/update-category-positions`,
        {
          items: reordered.map(({ id, position }) => ({ id, position })),
        }
      );
    } catch (error) {
      console.error("Failed to update positions", error);
    }
  };

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-start mb-4">
        <h2 className="mb-0 me-3">Categories</h2>
        <Link
          to="/xl-admin/add-category"
          className="btn btn-inchworm btn-sm"
          role="button"
          style={{
            padding: "0.25rem 0.4rem",
            fontSize: "0.8rem",
            lineHeight: "1",
          }}
        >
          Add New
        </Link>
      </div>

      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <label className="form-label">Parent Category</label>
          <select
            className="form-select"
            value={selectedParentId}
            onChange={(e) => setSelectedParentId(e.target.value)}
            disabled={loading}
          >
            <option value="">Select a Parent</option>
            {filterByTaxonomy(categories).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {selectedParentId && children.length > 0 && (
          <div className="col-md-4 mb-3">
            <label className="form-label">Subcategory</label>
            <select
              className="form-select"
              value={selectedChildId}
              onChange={(e) => setSelectedChildId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select a Subcategory</option>
              {filterByTaxonomy(children).map((child) => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedChildId && grandchildren.length > 0 && (
          <div className="col-md-4 mb-3">
            <label className="form-label">Sub-subcategory</label>
            <select
              className="form-select"
              value={selectedGrandchildId}
              onChange={(e) => setSelectedGrandchildId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select a Sub-subcategory</option>
              {filterByTaxonomy(grandchildren).map((grandchild) => (
                <option key={grandchild.id} value={grandchild.id}>
                  {grandchild.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : displayItems && displayItems.length > 0 ? (
        <div className="table-responsive mt-4">
          <table className="table table-bordered table-striped">
            <thead className="table-success">
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Type</th>
                <th>Premium</th>
                <th>Level</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((item, index) => (
                <tr
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{ cursor: "move" }}
                >
                  <td>{item.id}</td>
                  <td>{item.name}</td>
                  <td>{item.slug}</td>
                  <td>{item.taxonomy || "—"}</td>
                  <td>{item.premium === 1 ? "Yes" : "No"}</td>
                  <td>{item.position}</td>
                  <td>
                    <Link
                      to={`/xl-admin/edit-category?id=${item.id}`}
                      className="btn btn-sm btn-warning me-2"
                    >
                      Edit
                    </Link>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-muted">No categories found</div>
      )}
    </div>
  );
};

export default Categories;
